import { PriceSummaryItemKind, PriceSummary } from "common/types/priceSummary";
import { Incentive, Rebate } from "common/types/promotions";

import { toDollars } from "./formatting";
import { Optional } from "./types";

import { isValueSet } from ".";

const shippingToDisplay = (amount: PriceSummary["shipping"], isFinal: boolean, isFree: boolean) => {
  // We usually show this if the user hasn't finished making shipping selections
  if ((!isValueSet(amount) || !isFinal) && !isFree) {
    return "To be calculated";
  }

  // but if we know the cart total is already over the free shipping threshold
  // we can start showing 'FREE' even before that
  if (!amount || isFree) {
    return "FREE";
  }

  return toDollars(amount);
};

const taxToDisplay = (amount: PriceSummary["tax"]) =>
  isValueSet(amount) ? toDollars(amount) : "To be calculated";

export interface SummaryItem {
  kind: PriceSummaryItemKind;
  label: string;
  amount: Optional<number>;
  value: string;
  subrows?: Omit<SummaryItem, "kind">[];
  incentive?: Optional<Incentive>;
  rebate?: Optional<Rebate>;
}

export const getSummaryItems = <T extends PriceSummary>({
  subtotal,
  shipping,
  client_handling_fees,
  client_purchasing_fee,
  discount,
  tax,
  total,
  discount_breakdown,
  is_tax_final = true,
  is_shipping_final = true,
  is_shipping_free = false,
  is_all_pickup = false,
  total_discount_label = "Total discount",
  total_label = "Total",
  subtotal_label = "Subtotal",
  include_tax_if_unset = true,
}: T): SummaryItem[] => {
  const summaryItems = [
    {
      kind: PriceSummaryItemKind.Subtotal,
      label: subtotal_label,
      amount: subtotal,
      toDisplay: toDollars,
    },
    ...(is_all_pickup
      ? []
      : [
          {
            kind: PriceSummaryItemKind.Shipping,
            label: "Shipping",
            amount: shipping,
            toDisplay: (amount: PriceSummary["shipping"]) =>
              shippingToDisplay(amount, is_shipping_final, is_shipping_free),
          },
        ]),
    ...(client_handling_fees
      ? [
          {
            kind: PriceSummaryItemKind.ClientHandlingFees,
            label: "Handling",
            amount: client_handling_fees,
            toDisplay: toDollars,
          },
        ]
      : []),
    ...(client_purchasing_fee
      ? [
          {
            kind: PriceSummaryItemKind.ClientPurchasingFee,
            label: "Service fee",
            amount: client_purchasing_fee,
            toDisplay: toDollars,
          },
        ]
      : []),
    ...(discount
      ? [
          {
            kind: PriceSummaryItemKind.Discount,
            label: total_discount_label,
            amount: -discount,
            toDisplay: toDollars,
            subrows: discount_breakdown
              ? discount_breakdown.map((breakdown) => ({
                  label: breakdown.display,
                  amount: -breakdown.discount,
                  value: toDollars(-breakdown.discount),
                  incentive: breakdown.incentive,
                  rebate: breakdown.rebate,
                }))
              : [],
          },
        ]
      : []),
    ...(isValueSet(tax) || include_tax_if_unset
      ? [
          {
            kind: PriceSummaryItemKind.Tax,
            label: is_tax_final ? "Tax" : "Estimated tax",
            amount: tax,
            toDisplay: taxToDisplay,
          },
        ]
      : []),
    {
      kind: PriceSummaryItemKind.Total,
      label: total_label,
      amount: total,
      toDisplay: toDollars,
    },
  ];
  return summaryItems.map(({ amount, toDisplay, ...rest }) => ({
    ...rest,
    amount,
    value: toDisplay(amount),
  }));
};
